import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { addProspect } from "../lib/api";

const INPUT_TYPE = { number: "number", date: "date", email: "email", url: "url" };

// Type one contact in. The company decides (Settings → Contacts) whether it still waits for approval.
export default function AddContactModal({ fields, requiresApproval, close, onAdded }) {
  const [form, setForm] = useState({ name: "", role: "", company: "", email: "", phone: "", city: "", linkedin_url: "", notes: "" });
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const filled = Object.fromEntries(Object.entries(custom).filter(([, v]) => v !== "" && v !== undefined));
      const { created, prospect } = await addProspect({ ...form, custom: filled });
      onAdded(prospect);
      toast.success(created ? `${prospect.name} added${prospect.status === "Approved" ? " and ready for a sequence" : " — needs approval"}` : `${prospect.name} already existed — filled in what was missing`);
      close();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add the contact");
    } finally { setBusy(false); }
  };

  const customInput = (f) => {
    const v = custom[f.key] ?? "", put = (x) => setCustom(c => ({ ...c, [f.key]: x }));
    if (f.type === "dropdown") return <select value={v} onChange={e => put(e.target.value)}><option value="">—</option>{f.options.map(o => <option key={o}>{o}</option>)}</select>;
    if (f.type === "yes_no") return <select value={v} onChange={e => put(e.target.value)}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select>;
    return <input type={INPUT_TYPE[f.type] || "text"} value={v} onChange={e => put(e.target.value)} />;
  };

  return <div className="modal-backdrop" onClick={close}>
    <div className="modal login-card setup-card" onClick={e => e.stopPropagation()} data-testid="add-contact-modal">
      <button className="modal-close" onClick={close}><X size={16} /></button>
      <h2>Add a contact</h2>
      <p>{requiresApproval ? "This contact will wait for approval before any sequence runs." : "This contact is ready for a sequence straight away."}</p>
      <label>Name *<input value={form.name} onChange={set("name")} autoFocus data-testid="add-contact-name" /></label>
      <div className="two-col">
        <label>Job title<input value={form.role} onChange={set("role")} /></label>
        <label>Company<input value={form.company} onChange={set("company")} /></label>
      </div>
      <div className="two-col">
        <label>Email<input type="email" value={form.email} onChange={set("email")} data-testid="add-contact-email" /></label>
        <label>Phone (with country code)<input value={form.phone} onChange={set("phone")} placeholder="+61 400 000 000" /></label>
      </div>
      <div className="two-col">
        <label>City<input value={form.city} onChange={set("city")} /></label>
        <label>LinkedIn<input value={form.linkedin_url} onChange={set("linkedin_url")} /></label>
      </div>
      {fields.length > 0 && <fieldset className="key-group"><legend>Your fields</legend>
        {fields.map(f => <label key={f.key}>{f.label}{customInput(f)}</label>)}
      </fieldset>}
      <label>Notes<textarea value={form.notes} onChange={set("notes")} rows={3} /></label>
      <button className="primary-button full" onClick={save} disabled={busy || !form.name.trim()} data-testid="add-contact-save">{busy ? "Adding…" : "Add contact"}</button>
    </div>
  </div>;
}
